import { useEffect, useState } from "react";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Plus, Trash, PencilSimple, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

// Common Indian states (ISO 3166-2) — kept short; user can override state_code manually
const IN_STATES = [
  ["IN-MH", "Maharashtra"], ["IN-KA", "Karnataka"], ["IN-TN", "Tamil Nadu"],
  ["IN-WB", "West Bengal"], ["IN-TG", "Telangana"], ["IN-AP", "Andhra Pradesh"],
  ["IN-GJ", "Gujarat"], ["IN-KL", "Kerala"], ["IN-OD", "Odisha"],
  ["IN-MP", "Madhya Pradesh"], ["IN-PB", "Punjab"], ["IN-AS", "Assam"],
  ["IN-DL", "Delhi"], ["IN-HR", "Haryana"], ["IN-UP", "Uttar Pradesh"],
  ["IN-RJ", "Rajasthan"], ["IN-UT", "Uttarakhand"], ["IN-GA", "Goa"],
  ["IN-CH", "Chandigarh"], ["IN-CT", "Chhattisgarh"], ["IN-JK", "J&K"],
  ["IN-HP", "Himachal Pradesh"], ["IN-BR", "Bihar"], ["IN-JH", "Jharkhand"],
  ["IN-SK", "Sikkim"], ["IN-MZ", "Mizoram"], ["IN-MN", "Manipur"],
  ["IN-NL", "Nagaland"], ["IN-ML", "Meghalaya"], ["IN-AR", "Arunachal"],
  ["IN-TR", "Tripura"], ["IN-LA", "Ladakh"],
];
const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

export default function StatutoryConfig() {
  const [tab, setTab] = useState("lwf");
  return (
    <AppShell title="Statutory Configuration">
      <div className="flex items-center gap-1 mb-5 border-b border-zinc-200 overflow-x-auto">
        {[
          {k:"lwf", l:"LWF"},
          {k:"pt", l:"Professional Tax"},
          {k:"esic", l:"ESIC"},
          {k:"pf_admin", l:"PF Admin"},
          {k:"benefits", l:"Employer Benefits"},
        ].map(t=>(
          <button
            key={t.k}
            data-testid={`stat-tab-${t.k}`}
            onClick={()=>setTab(t.k)}
            className={`px-4 py-2 text-sm -mb-px border-b-2 whitespace-nowrap ${tab===t.k?"border-zinc-950 text-zinc-950 font-medium":"border-transparent text-zinc-500 hover:text-zinc-900"}`}>
            {t.l}
          </button>
        ))}
      </div>
      {tab === "lwf" && <LWFTab/>}
      {tab === "pt" && <PTTab/>}
      {tab === "esic" && <ESICTab/>}
      {tab === "pf_admin" && <PFAdminTab/>}
      {tab === "benefits" && <BenefitsTab/>}
    </AppShell>
  );
}

// =====================================================================
// LWF
// =====================================================================
function LWFTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);

  const load = () => api.get("/statutory-config/lwf").then(r=>setRows(r.data));
  useEffect(()=>{ load(); },[]);

  const blank = () => ({
    state_code:"IN-MH", state_name:"Maharashtra", frequency:"half_yearly",
    employee_amount:25, employer_amount:75, wage_ceiling:null,
    applicable_months:[6,12], notes:"", is_active:true,
  });

  const save = async () => {
    try {
      await api.post("/statutory-config/lwf", f);
      toast.success("LWF rule saved");
      setOpen(false); load();
    } catch(e){ toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if(!window.confirm("Delete this LWF rule?")) return;
    try { await api.delete(`/statutory-config/lwf/${id}`); load(); toast.success("Deleted"); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  const toggleMonth = (m) => {
    const cur = new Set(f.applicable_months||[]);
    if (cur.has(m)) cur.delete(m); else cur.add(m);
    setF({...f, applicable_months: Array.from(cur).sort((a,b)=>a-b)});
  };

  return (
    <SectionCard
      title="Labour Welfare Fund"
      subtitle="Configure per-state LWF rules with frequency, wage ceiling and applicable months."
      right={<Button data-testid="lwf-add-btn" onClick={()=>{setF(blank()); setOpen(true);}}><Plus size={14} className="mr-1"/>Add rule</Button>}
    >
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>State</TableHead>
            <TableHead>Cycle</TableHead>
            <TableHead>Employee</TableHead>
            <TableHead>Employer</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Months</TableHead>
            <TableHead></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map(r=>(
            <TableRow key={r.id}>
              <TableCell><Badge variant="outline">{r.state_code}</Badge> {r.state_name}</TableCell>
              <TableCell className="capitalize">{(r.cycle||"—").replace("_"," ")}</TableCell>
              <TableCell>{inr(r.employee_amount)}</TableCell>
              <TableCell>{inr(r.employer_amount)}</TableCell>
              <TableCell>{r.applicable ? <Badge>Active</Badge> : <Badge variant="outline">Off</Badge>}</TableCell>
              <TableCell className="text-xs">{(r.deduction_months||[]).map(m=>MONTHS[m-1]).join(", ") || "—"}</TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" data-testid={`lwf-edit-${r.state_code}`} onClick={()=>{setF({...r}); setOpen(true);}}><PencilSimple size={14}/></Button>
                <Button size="sm" variant="ghost" data-testid={`lwf-del-${r.state_code}`} onClick={()=>del(r.id)}><Trash size={14}/></Button>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={7} className="text-center text-zinc-400 py-6">No LWF rules configured. Click &quot;Add rule&quot; to start.</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      {f && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>LWF rule</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>State</Label>
                <Select value={f.state_code} onValueChange={(v)=>{
                  const s = IN_STATES.find(x=>x[0]===v);
                  setF({...f, state_code:v, state_name: s? s[1] : f.state_name});
                }}>
                  <SelectTrigger data-testid="lwf-state"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {IN_STATES.map(([c,n])=>(<SelectItem key={c} value={c}>{n} ({c})</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Cycle</Label>
                <Select value={f.cycle} onValueChange={(v)=>setF({...f, cycle:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="half_yearly">Half-yearly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Employee ₹</Label>
                <Input type="number" data-testid="lwf-emp-amt" value={f.employee_amount} onChange={e=>setF({...f, employee_amount: parseFloat(e.target.value)||0})}/>
              </div>
              <div>
                <Label>Employer ₹</Label>
                <Input type="number" data-testid="lwf-empr-amt" value={f.employer_amount} onChange={e=>setF({...f, employer_amount: parseFloat(e.target.value)||0})}/>
              </div>
              <div className="col-span-2">
                <Label>Deduction months</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {MONTHS.map((mn, i)=>{
                    const m = i+1;
                    const on = (f.deduction_months||[]).includes(m);
                    return (
                      <button key={m} type="button" onClick={()=>toggleMonth(m)}
                        className={`px-2 py-1 text-xs rounded border ${on?"bg-zinc-900 text-white border-zinc-900":"bg-white text-zinc-600 border-zinc-300"}`}>{mn}</button>
                    );
                  })}
                </div>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Input value={f.notes||""} onChange={e=>setF({...f, notes:e.target.value})}/>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Switch checked={f.applicable} onCheckedChange={v=>setF({...f, applicable:v})}/>
                <span className="text-sm">Applicable</span>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button data-testid="lwf-save" onClick={save}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </SectionCard>
  );
}

// =====================================================================
// Professional Tax
// =====================================================================
function PTTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const load = () => api.get("/statutory-config/pt").then(r=>setRows(r.data));
  const [defaults, setDefaults] = useState(null);
  useEffect(()=>{ load(); api.get("/statutory-config/pt/defaults").then(r=>setDefaults(r.data)).catch(()=>{}); },[]);
  const loadDefaults = async () => {
    try { const r = await api.post("/statutory-config/pt/load-defaults"); toast.success(`Copied ${r.data.created} built-in state rule${r.data.created === 1 ? "" : "s"} — edit them below`); load(); }
    catch { /* global toast */ }
  };

  const blank = () => ({
    state_code:"IN-MH", state_name:"Maharashtra", gender:"any",
    slabs:[{upper_inclusive:7500,amount:0},{upper_inclusive:10000,amount:175},{upper_inclusive:null,amount:200}],
    month_surcharges:[{month:2,amount:300,only_if_top_slab:true}],
    notes:"", is_active:true,
  });

  const save = async () => {
    try { await api.post("/statutory-config/pt", f); toast.success("PT rule saved"); setOpen(false); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if(!window.confirm("Delete this PT rule?")) return;
    try { await api.delete(`/statutory-config/pt/${id}`); load(); toast.success("Deleted"); }
    catch(e){ toast.error(formatApiError(e)); }
  };

  const updateSlab = (i, patch) => {
    const s = [...(f.slabs||[])]; s[i] = {...s[i], ...patch}; setF({...f, slabs:s});
  };
  const addSlab = () => setF({...f, slabs:[...(f.slabs||[]), {upper_inclusive:null, amount:0}]});
  const rmSlab = (i) => setF({...f, slabs:(f.slabs||[]).filter((_,j)=>j!==i)});

  const updateSurch = (i, patch) => {
    const s = [...(f.month_surcharges||[])]; s[i] = {...s[i], ...patch}; setF({...f, month_surcharges:s});
  };
  const addSurch = () => setF({...f, month_surcharges:[...(f.month_surcharges||[]), {month:2, amount:0, only_if_top_slab:true}]});
  const rmSurch = (i) => setF({...f, month_surcharges:(f.month_surcharges||[]).filter((_,j)=>j!==i)});

  return (
    <SectionCard
      title="Professional Tax slabs"
      subtitle="Per-state PT slabs. Add gender-specific rules and month surcharges (e.g. Maharashtra Feb ₹300)."
      right={<Button data-testid="pt-add-btn" onClick={()=>{setF(blank()); setOpen(true);}}><Plus size={14} className="mr-1"/>Add rule</Button>}
    >
      <Table>
        <TableHeader><TableRow>
          <TableHead>State</TableHead><TableHead>Gender</TableHead>
          <TableHead>Slabs</TableHead><TableHead>Month surcharges</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map(r=>(
            <TableRow key={r.id}>
              <TableCell><Badge variant="outline">{r.state_code}</Badge> {r.state_name}</TableCell>
              <TableCell className="capitalize">{r.gender}</TableCell>
              <TableCell className="text-xs">{(r.slabs||[]).map(s=>`≤${s.upper_inclusive??"∞"}:₹${s.amount}`).join(" · ")}</TableCell>
              <TableCell className="text-xs">{(r.month_surcharges||[]).map(s=>`${MONTHS[s.month-1]}:₹${s.amount}`).join(", ") || "—"}</TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" data-testid={`pt-edit-${r.state_code}-${r.gender}`} onClick={()=>{setF({...r}); setOpen(true);}}><PencilSimple size={14}/></Button>
                <Button size="sm" variant="ghost" onClick={()=>del(r.id)}><Trash size={14}/></Button>
              </TableCell>
            </TableRow>
          ))}
          {rows.length===0 && (<TableRow><TableCell colSpan={5} className="text-center text-zinc-400 py-6">No PT rules yet — payroll will use the built-in defaults shown below.</TableCell></TableRow>)}
        </TableBody>
      </Table>

      {defaults && (
        <div className="mt-6 rounded-md border border-zinc-200 bg-zinc-50 p-4" data-testid="pt-defaults">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <div className="text-sm font-semibold">Built-in defaults</div>
              <div className="text-xs text-zinc-500">Used for any state without a company rule. {defaults.fallback_note}</div>
            </div>
            <Button size="sm" variant="outline" onClick={loadDefaults} data-testid="pt-load-defaults">Copy into editable rules</Button>
          </div>
          <div className="mt-3 space-y-1 text-xs">
            {defaults.states.map(st => (
              <div key={st.state_code} data-testid={`pt-default-${st.state_code}`}>
                <Badge variant="outline">{st.state_code}</Badge>{" "}
                {st.slabs.map(x => `≤${x.upper_inclusive ?? "∞"}: ₹${x.amount}`).join(" · ")}
                {st.month_surcharges.length > 0 && ` · ${st.month_surcharges.map(m => `${MONTHS[m.month - 1]}: ₹${m.amount}`).join(", ")}`}
              </div>
            ))}
            <div className="text-zinc-500 pt-1">No PT: {defaults.no_pt_states.join(", ")}</div>
          </div>
        </div>
      )}

      {f && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>PT rule</DialogTitle></DialogHeader>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>State</Label>
                <Select value={f.state_code} onValueChange={(v)=>{
                  const s = IN_STATES.find(x=>x[0]===v);
                  setF({...f, state_code:v, state_name: s? s[1] : f.state_name});
                }}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{IN_STATES.map(([c,n])=>(<SelectItem key={c} value={c}>{n} ({c})</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={f.gender} onValueChange={(v)=>setF({...f, gender:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Active</Label>
                <div className="mt-2"><Switch checked={f.is_active} onCheckedChange={v=>setF({...f, is_active:v})}/></div>
              </div>
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <Label>Slabs (upper inclusive → monthly ₹)</Label>
                <Button size="sm" variant="outline" onClick={addSlab}><Plus size={12}/>Add slab</Button>
              </div>
              {(f.slabs||[]).map((s,i)=>(
                <div key={i} className="grid grid-cols-12 gap-2 mb-1">
                  <Input className="col-span-5" placeholder="Upper (blank = ∞)" type="number" value={s.upper_inclusive ?? ""} onChange={e=>updateSlab(i,{upper_inclusive: e.target.value===""?null:parseFloat(e.target.value)})}/>
                  <Input className="col-span-5" placeholder="Amount ₹/mo" type="number" value={s.amount} onChange={e=>updateSlab(i,{amount:parseFloat(e.target.value)||0})}/>
                  <Button className="col-span-2" size="sm" variant="ghost" onClick={()=>rmSlab(i)}><Trash size={14}/></Button>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <div className="flex items-center justify-between mb-1">
                <Label>Month surcharges</Label>
                <Button size="sm" variant="outline" onClick={addSurch}><Plus size={12}/>Add surcharge</Button>
              </div>
              {(f.month_surcharges||[]).map((s,i)=>(
                <div key={i} className="grid grid-cols-12 gap-2 mb-1 items-center">
                  <Select value={String(s.month)} onValueChange={v=>updateSurch(i,{month:parseInt(v)})}>
                    <SelectTrigger className="col-span-3"><SelectValue/></SelectTrigger>
                    <SelectContent>{MONTHS.map((m,idx)=>(<SelectItem key={m} value={String(idx+1)}>{m}</SelectItem>))}</SelectContent>
                  </Select>
                  <Input className="col-span-4" placeholder="Amount ₹" type="number" value={s.amount} onChange={e=>updateSurch(i,{amount:parseFloat(e.target.value)||0})}/>
                  <label className="col-span-4 text-xs flex items-center gap-2">
                    <Switch checked={!!s.only_if_top_slab} onCheckedChange={v=>updateSurch(i,{only_if_top_slab:v})}/>
                    Only top slab
                  </label>
                  <Button className="col-span-1" size="sm" variant="ghost" onClick={()=>rmSurch(i)}><Trash size={14}/></Button>
                </div>
              ))}
            </div>

            <div className="mt-3">
              <Label>Notes</Label>
              <Input value={f.notes||""} onChange={e=>setF({...f, notes:e.target.value})}/>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button data-testid="pt-save" onClick={save}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </SectionCard>
  );
}

// =====================================================================
// ESIC
// =====================================================================
function ESICTab() {
  const [f, setF] = useState(null);
  useEffect(()=>{ api.get("/statutory-config/esic").then(r=>setF(r.data)); },[]);
  const save = async () => {
    try { const r = await api.post("/statutory-config/esic", f); setF(r.data); toast.success("ESIC config saved"); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  if (!f) return null;
  return (
    <SectionCard title="ESIC configuration" subtitle="Wage ceiling + contribution rates. Applied when employee gross ≤ ceiling.">
      <div className="grid grid-cols-3 gap-4 max-w-2xl">
        <div>
          <Label>Wage ceiling (₹/mo)</Label>
          <Input type="number" data-testid="esic-ceiling" value={f.wage_ceiling} onChange={e=>setF({...f, wage_ceiling:parseFloat(e.target.value)||0})}/>
        </div>
        <div>
          <Label>Employee rate (%)</Label>
          <Input type="number" step="0.01" data-testid="esic-emp-rate" value={f.employee_rate_pct} onChange={e=>setF({...f, employee_rate_pct:parseFloat(e.target.value)||0})}/>
        </div>
        <div>
          <Label>Employer rate (%)</Label>
          <Input type="number" step="0.01" data-testid="esic-empr-rate" value={f.employer_rate_pct} onChange={e=>setF({...f, employer_rate_pct:parseFloat(e.target.value)||0})}/>
        </div>
        <div className="col-span-3 flex items-center gap-2">
          <Switch checked={!!f.is_active} onCheckedChange={v=>setF({...f, is_active:v})}/>
          <span className="text-sm">Active</span>
        </div>
      </div>
      <div className="mt-4"><Button data-testid="esic-save" onClick={save}>Save ESIC config</Button></div>
    </SectionCard>
  );
}

// =====================================================================
// PF Admin
// =====================================================================
function PFAdminTab() {
  const [f, setF] = useState(null);
  useEffect(()=>{ api.get("/statutory-config/pf-admin").then(r=>setF(r.data)); },[]);
  const save = async () => {
    try { const r = await api.post("/statutory-config/pf-admin", f); setF(r.data); toast.success("PF admin config saved"); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  if (!f) return null;
  return (
    <SectionCard title="PF Admin configuration" subtitle="EPF ceiling, contribution rates and admin/EDLI/EPS split. Statutory components (PF, EMPF, EPS, EDLI, PF_ADMIN) pick these up automatically.">
      <div className="grid grid-cols-3 gap-4 max-w-3xl">
        <div>
          <Label>PF wage ceiling (₹/mo)</Label>
          <Input type="number" data-testid="pf-ceiling" value={f.pf_wage_ceiling} onChange={e=>setF({...f, pf_wage_ceiling:parseFloat(e.target.value)||0})}/>
        </div>
        <div>
          <Label>Employee PF (%)</Label>
          <Input type="number" step="0.01" value={f.employee_pf_rate_pct} onChange={e=>setF({...f, employee_pf_rate_pct:parseFloat(e.target.value)||0})}/>
        </div>
        <div>
          <Label>Employer PF (%)</Label>
          <Input type="number" step="0.01" value={f.employer_pf_rate_pct} onChange={e=>setF({...f, employer_pf_rate_pct:parseFloat(e.target.value)||0})}/>
        </div>
        <div>
          <Label>EPS (%)</Label>
          <Input type="number" step="0.01" value={f.eps_rate_pct} onChange={e=>setF({...f, eps_rate_pct:parseFloat(e.target.value)||0})}/>
        </div>
        <div>
          <Label>EDLI (%)</Label>
          <Input type="number" step="0.01" value={f.edli_rate_pct} onChange={e=>setF({...f, edli_rate_pct:parseFloat(e.target.value)||0})}/>
        </div>
        <div>
          <Label>Admin charges (%)</Label>
          <Input type="number" step="0.01" value={f.admin_rate_pct} onChange={e=>setF({...f, admin_rate_pct:parseFloat(e.target.value)||0})}/>
        </div>
        <div className="col-span-3 flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={!!f.apply_ceiling} onCheckedChange={v=>setF({...f, apply_ceiling:v})}/> Apply ceiling
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Switch checked={!!f.is_active} onCheckedChange={v=>setF({...f, is_active:v})}/> Active
          </label>
        </div>
      </div>
      <div className="mt-4"><Button data-testid="pf-save" onClick={save}>Save PF Admin config</Button></div>
    </SectionCard>
  );
}

// =====================================================================
// Employer benefits
// =====================================================================
function BenefitsTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const load = () => api.get("/statutory-config/benefits").then(r=>setRows(r.data));
  useEffect(()=>{ load(); },[]);
  const blank = () => ({
    name:"", code:"", frequency:"annual", amount:0, is_taxable:true, is_in_ctc:true,
    payout_months:[], applies_to_grades:[], notes:"", is_active:true,
  });
  const save = async () => {
    try { await api.post("/statutory-config/benefits", f); toast.success("Benefit saved"); setOpen(false); load(); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    if(!window.confirm("Delete this benefit?")) return;
    try { await api.delete(`/statutory-config/benefits/${id}`); load(); toast.success("Deleted"); }
    catch(e){ toast.error(formatApiError(e)); }
  };
  const toggleMonth = (m) => {
    const cur = new Set(f.payout_months||[]);
    if (cur.has(m)) cur.delete(m); else cur.add(m);
    setF({...f, payout_months: Array.from(cur).sort((a,b)=>a-b)});
  };
  return (
    <SectionCard
      title="Employer-borne benefits & bonuses"
      subtitle="Reusable named benefits (mediclaim, annual bonus, gift voucher, meal card etc.) with frequency & payout months."
      right={<Button data-testid="ben-add-btn" onClick={()=>{setF(blank()); setOpen(true);}}><Plus size={14} className="mr-1"/>Add benefit</Button>}
    >
      <Table>
        <TableHeader><TableRow>
          <TableHead>Name</TableHead><TableHead>Code</TableHead><TableHead>Frequency</TableHead>
          <TableHead>Amount</TableHead><TableHead>CTC?</TableHead><TableHead>Taxable?</TableHead>
          <TableHead>Payout months</TableHead><TableHead></TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {rows.map(r=>(
            <TableRow key={r.id}>
              <TableCell>{r.name}</TableCell>
              <TableCell><Badge variant="outline">{r.code}</Badge></TableCell>
              <TableCell className="capitalize">{r.frequency.replace("_"," ")}</TableCell>
              <TableCell>{inr(r.amount)}</TableCell>
              <TableCell>{r.is_in_ctc? "Yes" : "No"}</TableCell>
              <TableCell>{r.is_taxable? "Yes" : "No"}</TableCell>
              <TableCell className="text-xs">{(r.payout_months||[]).map(m=>MONTHS[m-1]).join(", ") || "—"}</TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" onClick={()=>{setF({...r}); setOpen(true);}}><PencilSimple size={14}/></Button>
                <Button size="sm" variant="ghost" onClick={()=>del(r.id)}><Trash size={14}/></Button>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (<TableRow><TableCell colSpan={8} className="text-center text-zinc-400 py-6">No benefits yet.</TableCell></TableRow>)}
        </TableBody>
      </Table>

      {f && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Employer benefit</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Name</Label>
                <Input data-testid="ben-name" value={f.name} onChange={e=>setF({...f, name:e.target.value})}/>
              </div>
              <div>
                <Label>Code (UPPER_SNAKE)</Label>
                <Input data-testid="ben-code" value={f.code} onChange={e=>setF({...f, code:e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g,"_")})}/>
              </div>
              <div>
                <Label>Frequency</Label>
                <Select value={f.frequency} onValueChange={v=>setF({...f, frequency:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="quarterly">Quarterly</SelectItem>
                    <SelectItem value="half_yearly">Half-yearly</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                    <SelectItem value="one_time">One-time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount ₹</Label>
                <Input type="number" value={f.amount} onChange={e=>setF({...f, amount:parseFloat(e.target.value)||0})}/>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={f.is_in_ctc} onCheckedChange={v=>setF({...f, is_in_ctc:v})}/>
                <span className="text-sm">Part of CTC</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={f.is_taxable} onCheckedChange={v=>setF({...f, is_taxable:v})}/>
                <span className="text-sm">Taxable</span>
              </div>
              <div className="col-span-2">
                <Label>Payout months (leave empty = auto)</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {MONTHS.map((mn, i)=>{
                    const m = i+1;
                    const on = (f.payout_months||[]).includes(m);
                    return (
                      <button key={m} type="button" onClick={()=>toggleMonth(m)}
                        className={`px-2 py-1 text-xs rounded border ${on?"bg-zinc-900 text-white border-zinc-900":"bg-white text-zinc-600 border-zinc-300"}`}>{mn}</button>
                    );
                  })}
                </div>
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Input value={f.notes||""} onChange={e=>setF({...f, notes:e.target.value})}/>
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <Switch checked={f.is_active} onCheckedChange={v=>setF({...f, is_active:v})}/>
                <span className="text-sm">Active</span>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
              <Button data-testid="ben-save" onClick={save}>Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </SectionCard>
  );
}
